export type ResearchIntakeKind = 'person' | 'company';

export type ResearchIntakeStageId = 'who' | 'identity' | 'followup';

export type ResearchIntakeFollowupSection = 'identifiers' | 'contact' | 'more';

export type ResearchIntakeStep = {
  id: ResearchIntakeStageId;
  labelEn: string;
  labelEs: string;
  optional?: boolean;
};

export function researchIntakeSteps(kind: ResearchIntakeKind | ''): ResearchIntakeStep[] {
  if (kind === 'person') {
    return [
      { id: 'identity', labelEn: 'Name', labelEs: 'Nombre' },
      { id: 'followup', labelEn: 'Optional details', labelEs: 'Datos opcionales', optional: true },
    ];
  }
  if (kind === 'company') {
    return [
      { id: 'identity', labelEn: 'Company', labelEs: 'Empresa' },
      { id: 'followup', labelEn: 'Optional details', labelEs: 'Datos opcionales', optional: true },
    ];
  }
  return [];
}

export function researchIntakeCurrentStage(
  kind: ResearchIntakeKind | '',
  followupOpen: boolean,
): ResearchIntakeStageId {
  if (!kind) {
    return 'who';
  }
  return followupOpen ? 'followup' : 'identity';
}

export function researchIntakeFollowupSections(
  kind: ResearchIntakeKind,
): ResearchIntakeFollowupSection[] {
  return kind === 'person' ? ['identifiers', 'contact', 'more'] : ['contact', 'more'];
}

/** Blank the new-search form when it still shows someone already sitting in the queue. */
export function researchIntakeMatchesActiveQueue(intakeName: string, queuedNames: string[]): boolean {
  const current = String(intakeName || '').trim().toLowerCase();
  if (!current) {
    return false;
  }
  return queuedNames.some((name) => String(name || '').trim().toLowerCase() === current);
}

/**
 * After a job is accepted, the New report form must not keep showing that subject.
 * Match queued, screening, and completed queue rows (failed restores stay filled on purpose).
 */
export function researchIntakeMatchesProcessedQueue(
  intakeName: string,
  queue: Array<{ targetName?: string; status?: string }>,
): boolean {
  const processedNames = queue
    .filter((item) => {
      const status = String(item.status || '').toLowerCase();
      return status === 'queued' || status === 'screening' || status === 'completed';
    })
    .map((item) => String(item.targetName || ''));
  return researchIntakeMatchesActiveQueue(intakeName, processedNames);
}

/**
 * Blank New report when a queued subject is still sitting in the form.
 * Do not blank while the operator is typing — adding a middle name is what
 * makes "First Last" become the full name already stored on a prior report.
 */
export function researchIntakeShouldClearForQueue(input: {
  tabIsResearch: boolean;
  editingExistingReport: boolean;
  operatorIsTyping: boolean;
  intakeName: string;
  queue: Array<{ targetName?: string; status?: string }>;
}): boolean {
  if (!input.tabIsResearch || input.editingExistingReport || input.operatorIsTyping) {
    return false;
  }
  return researchIntakeMatchesProcessedQueue(input.intakeName, input.queue);
}

export function researchIntakeSubjectNameStarted(
  kind: ResearchIntakeKind | '',
  keyData: {
    names?: string;
    website?: string;
    givenName?: string;
    givenName2?: string;
    surname?: string;
    surname2?: string;
  },
): boolean {
  if (kind === 'person') {
    return Boolean(
      String(keyData.givenName || '').trim()
      || String(keyData.givenName2 || '').trim()
      || String(keyData.surname || '').trim()
      || String(keyData.surname2 || '').trim()
      || String(keyData.names || '').trim(),
    );
  }
  if (kind === 'company') {
    return Boolean(
      String(keyData.names || '').trim()
      || String(keyData.website || '').trim(),
    );
  }
  return false;
}

export function researchIntakeClearedPersonFields<T extends {
  birthdate?: string;
  ageRange?: string;
  ageBasis?: 'birthdate' | 'ageRange';
  ssn?: string;
  dlNumber?: string;
  dlState?: string;
  gender?: string;
  formerNameStyle?: string;
  formerNameStyleLocked?: boolean;
  formerGivenName?: string;
  formerGivenName2?: string;
  formerSurname?: string;
  formerSurname2?: string;
  includeMaidenName?: boolean;
}>(keyData: T): T {
  return {
    ...keyData,
    birthdate: '',
    ageRange: '',
    ageBasis: 'birthdate',
    ssn: '',
    dlNumber: '',
    dlState: '',
    gender: '',
    includeMaidenName: false,
    formerNameStyle: '',
    formerNameStyleLocked: false,
    formerGivenName: '',
    formerGivenName2: '',
    formerSurname: '',
    formerSurname2: '',
  };
}
